import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	camelToSnake,
	IQueryParams,
	UrlService,
} from '@datachain/ui-sdk/common';
import { map, Observable } from 'rxjs';

import { DxFilterField, DxSortField } from '../../core/dx-types';
import { DxGridUtil } from '../../core/utils/dx-grid.util';
import { DcBaseRequester } from '../core/dc-base.requester';
import {
	GroupingTagFields,
	GroupingTagPageResponse,
	GroupingTagResponse,
	ProjectByGroupingTagCountResponse,
	ProjectIdLabelResponse,
} from './grouping-tag.model';

@Injectable({
	providedIn: 'root',
})
export class GroupingTagRequester extends DcBaseRequester<unknown> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;

	private readonly groupingTagFieldMapping: {
		[k: string]: GroupingTagFields;
	} = {
		label: GroupingTagFields.Code,
		description: GroupingTagFields.Description,
		color: GroupingTagFields.Color,
	};

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('GROUPING_TAGS');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = `${this.resource}/{id}`;
	}

	public createGroupingTag(label: string, color: string): Observable<void> {
		const resource = this.generateResourceUrl('GROUPING_TAGS');
		const url = this.urlService.generateUrl(resource, {}, {});
		return this.http.post<void>(url, {
			code: label,
			color,
		});
	}

	public updateGroupingTag(
		id: string,
		label: string,
		color: string
	): Observable<void> {
		const resource = this.generateResourceUrl('GROUPING_TAGS');
		const url = this.urlService.generateUrl(resource, {}, {});
		return this.http.post<void>(url, {
			id,
			code: label,
			color,
		});
	}

	public deleteGroupingTag(selected: string): Observable<void> {
		const resource = this.generateResourceUrl('GROUPING_TAGS_BY_ID');
		const url = this.urlService.generateUrl(
			resource,
			{
				id: selected,
			},
			{}
		);
		return this.http.delete<void>(url);
	}

	public getProjectsCountByGroupingTags(
		ids: Array<string>
	): Observable<ReadonlyArray<ProjectByGroupingTagCountResponse>> {
		const resource = this.generateResourceUrl(
			'PROJECTS_BY_GROUPING_TAG_BULK_COUNT'
		);
		const url = this.urlService.generateUrl(
			resource,
			{},
			{
				groupingTagsId: ids,
			}
		);
		return this.get<ReadonlyArray<ProjectByGroupingTagCountResponse>>(url);
	}

	public getReferencedProjectsByGroupingTagId(
		id: string
	): Observable<ReadonlyArray<ProjectIdLabelResponse>> {
		const resource = this.generateResourceUrl('PROJECTS_BY_GROUPING_TAG');
		const url = this.urlService.generateUrl(
			resource,
			{},
			{
				groupingTagId: id,
			}
		);

		return this.get<ReadonlyArray<ProjectIdLabelResponse>>(url);
	}

	public getGroupingTagPage(
		pageSize: number,
		pageIndex: number,
		search: string,
		filteredFields: Array<DxFilterField>,
		sortedFields: Array<DxSortField>,
		excludeInactive = false,
		withPermissions = false
	): Observable<GroupingTagPageResponse> {
		const resourceUrl = this.generateResourceUrl('GROUPING_TAGS');
		const filter = DxGridUtil.generateFilterFieldPayload(
			filteredFields,
			this.groupingTagFieldMapping
		);

		const sortParams = DxGridUtil.generateSortFieldPayload(
			sortedFields,
			this.groupingTagFieldMapping
		);

		const defaultQueryParams: IQueryParams = {
			page: pageIndex,
			size: pageSize,
			...filter,
			withPermissions,
		};

		const queryParams: IQueryParams = search
			? {
					...defaultQueryParams,
					['fullText.contains']: search,
			  }
			: defaultQueryParams;

		let params = new HttpParams({
			fromObject: queryParams as {
				[key: string]:
					| number
					| string
					| boolean
					| Array<number | string | boolean>;
			},
		});

		if (sortParams && sortParams.length > 0) {
			sortParams.forEach((s) => {
				params = params.append('sort', s);
			});
		}

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				excludeInactive,
			}
		);

		return this.http
			.get<ReadonlyArray<GroupingTagResponse>>(url, {
				params,
				observe: 'response',
			})
			.pipe(
				map((response) => ({
					elements: response.body ?? [],
					total: this.getTotal(response),
				}))
			);
	}

	private getTotal(
		response: HttpResponse<ReadonlyArray<GroupingTagResponse>>
	): number {
		return Number.parseInt(response.headers.get('x-total-count') ?? '0', 10);
	}
}
